import { Box, Stack } from '@mui/material'
import { Button, Container, Typography } from '@evocolabs/orkaui'

import Link from 'next/link'
import { SectionTitle } from '../components/title'
import { useRouter } from 'next/router'

interface NavigationSection {
  icon: React.ReactNode
  subtitle: string
  text: string
  CTAText: string
  CTALink?: string
  CTAIcon?: React.ReactNode
  CTAType: string
  CTAHref?: string
}

export interface NavigationProps {
  title: string
  titleColor?: string
  sections: NavigationSection[]
  pageId: string
  staticTitle?: boolean
}

const Navigation = ({
  title,
  titleColor,
  sections,
  pageId,
  staticTitle = true // title styles are different on product page and homepage, the later is responsive one
}: NavigationProps) => {
  const router = useRouter()
  const getGridTemplates = () => {
    const sectionsLen = sections.length
    return sectionsLen % 2 === 0
      ? {
          xs: '1fr / repeat(4, 1fr)',
          sm: 'repeat(2, 1fr) / repeat(2, 1fr)',
          dl: '1fr / repeat(4, 1fr)'
        }
      : {
          xs: `1fr / repeat(${sectionsLen}, 1fr)`
        }
  }
  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '40px'
      }}
    >
      <Box>
        {staticTitle ? (
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              color: titleColor
            }}
          >
            {title}
          </Typography>
        ) : (
          <SectionTitle color={titleColor}>{title}</SectionTitle>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gap: '24px',
          overflowX: { xs: 'scroll', sm: 'visible' },
          width: '100%',
          paddingBottom: '16px',
          '&::-webkit-scrollbar': {
            display: { xs: 'flex', sm: 'none' },
            height: '8px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#506A8A',
            borderRadius: '6px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#EBE2D3',
            borderRadius: '6px'
          },
          gridTemplate: getGridTemplates()
        }}
      >
        {sections.length > 0 &&
          sections.map(
            ({
              subtitle,
              icon,
              text,
              CTAText,
              CTAHref,
              CTAType,
              CTAIcon,
              CTALink
            }) => (
              <Stack
                alignItems="center"
                gap={{ xs: '12px', sm: 2 }}
                key={subtitle}
              >
                <Box
                  key={subtitle}
                  sx={{
                    display: 'flex',
                    minWidth: { xs: '240px', sm: '0px' },
                    flexDirection: 'column',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'start',
                    gap: '8px',
                    '> span': {
                      textAlign: { xs: 'left', sm: 'center' }
                    },
                    flex: 1
                  }}
                >
                  {icon}
                  <Typography variant="T2">{subtitle}</Typography>
                  <Typography variant="body3">{text}</Typography>
                </Box>
                <>
                  {CTAType === 'Button' && (
                    <Button
                      handleClick={() => {
                        router.push(CTAHref)
                      }}
                      id={`${pageId}-${CTAText.toLowerCase().replaceAll(
                        ' ',
                        '-'
                      )}`}
                      variant="secondary"
                      disabled={CTAText === 'Out of Stock'}
                    >
                      {CTAText}
                    </Button>
                  )}
                  {CTAType === 'Link' && (
                    <Link href={CTALink}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '48px'
                        }}
                      >
                        {CTAIcon}
                        <Typography variant="Link" color="#415670">
                          {CTAText}
                        </Typography>
                      </Box>
                    </Link>
                  )}
                </>
              </Stack>
            )
          )}
      </Box>
    </Container>
  )
}

export default Navigation
